import { CancelTokenSource } from 'axios'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import * as ChatDownloadUrl from '../services/ChatDownloadUrl'
import { api_base_path } from '../services/Constants'
import * as FileTypes from '../services/FileTypes'
import ipc, { DownloadProgressInfo, webUtils } from '../services/ElectronIPC'
import LocalChatFilesManifest from '../services/LocalChatFilesManifest'
import { ChatMessage, ChatRoomType } from '../types/ChatContracts'
import { passthroughNumber } from '../utils'
import Api, { FileMetadata } from '../services/Api'
import { getAuthTokenFromLocalUserCache } from '../Logic'
import { wsConnection } from '../services/WSConnectionInstance'
import useRoomContextRef from '../services/useRoomContextRef'
import { MuseWSMessage } from '../pages/CollaboratePage/Types'
import { env } from '../services/EnvironmentVariables'
import ElectronIPC from '../services/ElectronIPC'
import { logger } from '../services/Logger'
import { serializeError } from 'serialize-error'
import { AppContext, AppContextType } from '../AppContext'
import { useTranslation } from 'react-i18next'
import useUserContextRef from '@/services/useUserContextRef'

const log = logger.createChildLogger({ name: 'CentralizedChatManager' })

export interface FileUploadProgressData {
    loaded: number
    total: number
    error?: { code: string }
}

export interface ChatStats {
    fileSpaceUsage: number | null
    fileStorageSize: number | null
}

function useCentralizedChatManager({
    userUrn,
    roomUrn,
    roomType,
    isEnabled,
    isEmpty = false,
}: {
    userUrn: string
    roomUrn: string
    roomType: ChatRoomType
    isEnabled: boolean
    isEmpty?: boolean
}) {
    const { t } = useTranslation()

    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const [syncCalled, setSyncCalled] = useState<boolean>(false)
    const [isLoading, set_isLoading] = useState<boolean>(true)

    // the main message history (committed)
    const [messageHistory, set_messageHistory] = useState<ChatMessage[]>([])
    const messageHistoryRef = useRef<ChatMessage[]>([])

    // pending messages (sending text, file, etc)
    const [messagePendingLookup, set_messagePendingLookup] = useState<Map<string, ChatMessage>>(
        new Map<string, ChatMessage>()
    )
    const messagePendingLookupRef = useRef<Map<string, ChatMessage>>(new Map<string, ChatMessage>())

    const [fileUploadProgressLookup, set_fileUploadProgressLookup] = useState<Map<string, FileUploadProgressData>>(
        new Map<string, FileUploadProgressData>()
    )
    const [fileDownloadProgressLookup, set_fileDownloadProgressLookup] = useState<Map<string, DownloadProgressInfo>>(
        new Map<string, DownloadProgressInfo>()
    )
    const [fileUploadCancelTokenLookup, set_fileUploadCancelTokenLookup] = useState<Map<string, CancelTokenSource>>(
        new Map<string, CancelTokenSource>()
    )
    const [stats, set_stats] = useState<ChatStats>({ fileSpaceUsage: null, fileStorageSize: null })

    const participants = useRef<Map<string, { username: string; displayName?: string; isGuest: boolean }>>(new Map())

    const onFileDownloadComplete = useRef<(fileUuid: string, filename: string) => void>(() => {})
    const onNewMessage = useRef<(chatmsg: ChatMessage) => void>(() => {})

    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const messagePendingLookupSet = useCallback((key: string, value: ChatMessage) => {
        messagePendingLookupRef.current.set(key, value)
        set_messagePendingLookup((curr) => {
            const next = new Map(curr)
            next.set(key, value)
            return next
        })
    }, [])

    const messagePendingLookupDelete = useCallback((key: string) => {
        messagePendingLookupRef.current.delete(key)
        set_messagePendingLookup((curr) => {
            const next = new Map(curr)
            next.delete(key)
            return next
        })
    }, [])

    /**
     * Effect on component load
     */
    useEffect(() => {
        if (!isEnabled) {
            return
        }
        if (!roomUrn) {
            return
        }

        /// session.registerReceiveWSMessage(
        ///     ['send-chatmsg-ack', 'recv-chatmsg', 'get-chatmsg-history', 'chatmsg-update', 'deleted-chatmsg'],
        ///     onReceivedWSmessage
        /// )
        wsConnection.addEventListener('onmessage', onReceivedWSmessage)

        /**
         * Download progress handler
         */
        const downloadProgressHandler = (event: any, info: DownloadProgressInfo) => {
            console.log(`chatmsg: info=${JSON.stringify(info, null, '   ')}`)
            if (info.url == null) {
                throw new Error(
                    `download-dl progress callback's info object does not have the 'url' property! This is likely ` +
                        `because our custom fork of download-dl was not used. Ensure you're using the proper download-dl library.`
                )
            }

            const fileUuid = ChatDownloadUrl.fromUrlToFileUuid(info.url)
            if (!info.isDone) {
                set_fileDownloadProgressLookup((lookup) => {
                    const next = new Map(lookup)
                    next.set(fileUuid, info)
                    return next
                })
            } else {
                set_fileDownloadProgressLookup((lookup) => {
                    const next = new Map(lookup)
                    next.delete(fileUuid)
                    return next
                })

                LocalChatFilesManifest.add(fileUuid, { savePath: info.savePath })

                const messageHistory = messageHistoryRef.current
                const matchingMsg = messageHistory.filter(
                    (item) => item.contentType === 'file' && item.body.link === fileUuid
                )[0]
                if (matchingMsg && matchingMsg.contentType === 'file') {
                    onFileDownloadComplete.current(fileUuid, matchingMsg.body.filename)
                } else {
                    onFileDownloadComplete.current('', '')
                }
            }
        }

        if (isEmpty) {
            set_messageHistory([])
            set_isLoading(false)
        }

        ipc.registerDownloadProgressHandler(downloadProgressHandler)

        return () => {
            wsConnection.removeEventListener('onmessage', onReceivedWSmessage)
            ipc.removeDownloadProgressHandler(downloadProgressHandler)
        }
    }, [isEmpty, roomUrn])

    useEffect(() => {
        messageHistoryRef.current = messageHistory
    }, [messageHistory])

    /**
     * Process WS events from the central chat server.
     */
    const onReceivedWSmessage = async (msg: MuseWSMessage) => {
        const { type, data } = msg
        if (type === 'recv-chatmsg') {
            const chatmsg: ChatMessage = data.chatmsg
            chatmsg.timestamp = moment(chatmsg.timestamp).toDate()

            const msgDestinationRoomUrn = data.roomUrn
            // NOTE: There is a possibility that the message's intended receiving room
            // is not the current room.
            if (roomUrn !== msgDestinationRoomUrn) {
                // ignore this message
                return
            }

            // console.log(`*** recv-chatmsg: %O`, data)
            // console.log(`*** recv-chatmsg: uuid=${data.uuid} messagePendingLookupRef=%O`, messagePendingLookupRef.current)

            if (messagePendingLookupRef.current.has(data.uuid)) {
                // this is an "ack" of the message that we have sent previously.
                messagePendingLookupDelete(data.uuid)
                set_fileUploadProgressLookup((lookup) => {
                    const next = new Map(lookup)
                    next.delete(data.uuid)
                    return next
                })
                set_messageHistory((history) => mergeIntoMessageHistory([...history], chatmsg))
            } else {
                set_messageHistory((history) => mergeIntoMessageHistory([...history], chatmsg))

                if (chatmsg?.senderUrn !== userUrn) {
                    onNewMessage.current(chatmsg)
                }
            }

            if (data.stats) {
                set_stats((stats) => {
                    const next = { ...stats }
                    next.fileSpaceUsage = passthroughNumber(data.stats.fileSpaceUsage)
                    next.fileStorageSize = passthroughNumber(data.stats.fileStorageSize)
                    return next
                })
            }

            console.log(`at onReceivedWSMessage: messageProgressLookup=%O data=%O`, fileUploadProgressLookup, data)
        } else if (type === 'get-chatmsg-history') {
            const acc: ChatMessage[] = []
            // same as 'recv-chatmsg' except it is a list of chatmsgs, and is never
            // an 'ack' of the message that was sent previously.
            const { chatHistory, stats, roomUrn: msgDestinationRoomUrn } = data

            // NOTE: There is a possibility that the message's intended receiving room
            // is not the current room.
            if (roomUrn !== msgDestinationRoomUrn) {
                // ignore this message
                return
            }

            for (let participant of data.participants) {
                participants.current.set(participant._id, {
                    username: participant.username,
                    displayName: participant.displayName,
                    isGuest: participant.isGuest,
                })
            }

            for (let chatmsg_ of chatHistory) {
                let chatmsg: ChatMessage = chatmsg_

                // backwards compatibility -- older versions do not have the 'size' field.
                if (chatmsg.contentType === 'file' && !chatmsg.body.size) {
                    chatmsg.body.size = 0
                }
                chatmsg.timestamp = moment(chatmsg.timestamp).toDate()
                // modifies `acc` in-place.
                mergeIntoMessageHistory(acc, chatmsg)
            }
            set_messageHistory(acc)
            set_isLoading(false)

            if (stats) {
                set_stats((stats) => {
                    const next = { ...stats }
                    next.fileSpaceUsage = passthroughNumber(data.stats.fileSpaceUsage)
                    next.fileStorageSize = passthroughNumber(data.stats.fileStorageSize)
                    return next
                })
            }
        } else if (type === 'deleted-chatmsg') {
            const { uuids, stats } = data
            console.log('deleted-chatmsg: uuids=' + JSON.stringify(uuids))

            set_messageHistory((h) => {
                const next = h.filter((item) => !uuids.includes(item.id))
                console.log('deleted-chatmsg: messageHistory after deletion: %O', h)
                return next
            })

            if (stats) {
                set_stats((stats) => {
                    const next = { ...stats }
                    next.fileSpaceUsage = passthroughNumber(data.stats.fileSpaceUsage)
                    next.fileStorageSize = passthroughNumber(data.stats.fileStorageSize)
                    return next
                })
            }
        }
    }

    const sendText = (message: string) => {
        if (!isEnabled) {
            return
        }

        const chatmsg: ChatMessage = {
            id: '',
            timestamp: new Date(),
            senderUrn: userUrn,
            recipientUrns: [],
            contentType: 'text',
            body: message,
        }

        const tempUuid = uuidv4()
        messagePendingLookupSet(tempUuid, chatmsg)

        wsConnection.sendMessage({
            type: 'send-chatmsg',
            data: {
                uuid: tempUuid,
                senderUrn: userUrn,
                roomUrn: roomUrn,
                roomType: roomType,
                chatmsg: chatmsg,
            },
        })
    }

    const sendFile = (files: File[]) => {
        if (!isEnabled) {
            return
        }

        // handle more than one file recursively
        const file: File = files[0]
        if (files.length > 1) {
            for (let i = 0; i < files.length; ++i) {
                sendFile([files[i]])
            }
            return
        }

        const filePath = webUtils.getPathForFile(file)

        console.log(`sendFile: file=${JSON.stringify(file)}`)

        const fileData: FileMetadata = {
            filePath: filePath,
            filename: file.name || '',
            filesize: file.size || 0,
            mimetype: file.type || '',
        }

        const chatmsg: ChatMessage = {
            id: '',
            timestamp: new Date(),
            senderUrn: userUrn,
            recipientUrns: [],
            contentType: 'file',
            body: {
                link: '',
                filename: fileData.filename,
                caption: '',
                size: fileData.filesize,
                mimeType: fileData.mimetype,
            },
        }
        const tempUuid = uuidv4()
        messagePendingLookupSet(tempUuid, chatmsg)
        set_fileUploadProgressLookup((lookup) => {
            const next = new Map(lookup)
            next.set(tempUuid, { loaded: 0, total: 0 })
            return next
        })

        const cancelToken = Api.createUploadedFile(
            roomUrn,
            roomType,
            file,
            fileData,
            // onProgress
            (ev: any) => {
                const { loaded, total } = ev
                set_fileUploadProgressLookup((lookup) => {
                    const next = new Map(lookup)
                    next.set(tempUuid, { loaded, total })
                    return next
                })
            },
            // onEnd
            async (result: any, err: any) => {
                if (err) {
                    if (err.code !== 'canceled') {
                        log.error('sendFile', `Api.createUploadedFile returned error:`, {
                            result: result,
                            error: serializeError(err),
                            params: { roomUrn, roomType, fileData },
                        })
                    }

                    const fileHasChanged = await (async function detectFileChanged() {
                        try {
                            if (!fileData.filePath) {
                                return false
                            }

                            const fileStatResult = await ElectronIPC.getFileStats(fileData.filePath)
                            const currentFileSize = fileStatResult?.size
                            if (currentFileSize) {
                                if (currentFileSize !== fileData.filesize) {
                                    return { currentFileSize, previousFileSize: fileData.filesize }
                                } else {
                                    return false
                                }
                            }
                        } catch (e) {
                            return false
                        }
                    })()

                    if (fileHasChanged) {
                        log.error(
                            'sendFile',
                            `detected file has changed. setting err.code="file_changed". ` +
                                `info=${JSON.stringify(fileHasChanged)}.`
                        )

                        err.code = 'file_changed'

                        appDispatch({
                            type: 'new_message_dialog',
                            value: {
                                id: 'chatbox_file_changed_notification',
                                text:
                                    'It looks like the file has changed during the upload. ' +
                                    'This can happen when a file is being actively written to or edited ' +
                                    'by another program.\n' +
                                    'To mitigate this problem, you can make a static ' +
                                    'copy of the file and then upload that file.',
                                negativeButtonDoNotShowAgain: true,
                            },
                        })
                    }

                    set_fileUploadProgressLookup((lookup) => {
                        const uploadItem = lookup.get(tempUuid)
                        if (uploadItem) {
                            const next = new Map(lookup)
                            uploadItem.error = err
                            next.set(tempUuid, uploadItem)
                            return next
                        } else {
                            return lookup
                        }
                    })
                } else {
                    chatmsg.body.link = result.uuid
                    LocalChatFilesManifest.add(result.uuid, { savePath: filePath })

                    messagePendingLookupSet(tempUuid, chatmsg)

                    wsConnection.sendMessage({
                        type: 'send-chatmsg',
                        data: {
                            uuid: tempUuid,
                            senderUrn: userUrn,
                            roomUrn: roomUrn,
                            roomType: roomType,
                            chatmsg: chatmsg,
                        },
                    })
                }

                set_fileUploadCancelTokenLookup((lookup) => {
                    const next = new Map(lookup)
                    next.delete(tempUuid)
                    return next
                })
            }
        )

        set_fileUploadCancelTokenLookup((lookup) => {
            const next = new Map(lookup)
            next.set(tempUuid, cancelToken)
            return next
        })
    }

    const deleteMessages = (uuids: string[]) => {
        if (!isEnabled) {
            return
        }

        set_messageHistory((h) => {
            return h.filter((item) => !uuids.includes(item.id))
        })

        wsConnection.sendMessage({
            type: 'delete-chatmsgs',
            data: {
                senderUrn: userUrn,
                roomUrn: roomUrn,
                roomType: roomType,
                uuids: uuids,
            },
        })
    }

    const cancelFileUpload = (id: string) => {
        messagePendingLookupDelete(id)
        set_fileUploadProgressLookup((lookup) => {
            const next = new Map(lookup)
            next.delete(id)
            return next
        })

        const token = fileUploadCancelTokenLookup.get(id)
        console.log(`at cancelFileUpload: got cancel token=%O`, token)
        if (token) {
            console.log(`at cancelFileUpload: calling cancel()`)
            token.cancel()
            console.log(`at cancelFileUpload: cancel() returned.`)
        }
    }

    const cancelFileDownload = (id: string) => {
        set_fileDownloadProgressLookup((lookup) => {
            const next = new Map(lookup)
            next.delete(id)
            return next
        })

        // TODO: actually cancel the download.
    }

    const loadMessages = async () => {
        if (!isEnabled) {
            return
        }

        const authToken = getAuthTokenFromLocalUserCache()
        wsConnection.sendMessage({
            type: 'get-chatmsg-history',
            data: {
                authToken: authToken,
                senderUrn: userUrn,
                roomUrn: roomUrn,
                roomType: roomType,
            },
        })

        setSyncCalled(true)
    }

    const triggerDownloadFile = async (
        filename: string,
        fileSize: number,
        fileUuid: string,
        actionAfter: 'none' | 'open' | 'show-in-folder'
    ) => {
        const url = ChatDownloadUrl.fromFileUuidToUrl(api_base_path, roomUrn, roomType, fileUuid)

        if (env.IS_ELECTRON) {
            console.log(`at triggerDownloadFile: fileUuid=${fileUuid}`)

            const localCache = LocalChatFilesManifest.get(fileUuid)
            let localCachePath: string | null = null
            if (localCache) {
                console.log(`at triggerDownloadFile: localCache exists: ${JSON.stringify(localCache)}`)
                if ((await FileTypes.verifyFile(localCache.savePath)) === true) {
                    console.log(`at triggerDownloadFile: localCache exists and is verifed.`)
                    // Local file exists and is verified fresh; no-op.
                    localCachePath = localCache.savePath
                } else {
                    // Local file exists but is not the same file. Invalidate cache and download.
                    console.log(
                        `at triggerDownloadFile: Local file exists but is not the same file. Invalidate cache and download.`
                    )
                    LocalChatFilesManifest.invalidate(fileUuid)
                }
            }

            // The electron-dl's index.js has the line "window_.webContents.downloadURL(url)" where the second
            // param to downloadURL can take in options as defined in
            // <https://www.electronjs.org/docs/api/web-contents#contentsloadurlurl-options>. Consider adding
            // the headers there and contribute.
            ipc.downloadFile(url, localCachePath, actionAfter)
        } else {
            ElectronIPC.openLinkInBrowser(url)
        }
    }

    const resolveMessageUsername = useCallback(
        (peerUserUrn: string) => {
            if (!isEnabled) {
                return ''
            }

            if (peerUserUrn === userUrn) {
                if (userStateRef.current.isGuest) {
                    return `${userStateRef.current.profile?.displayName || userStateRef.current.profile?.username} (You)`
                } else {
                    return `${userStateRef.current.profile?.username} (You)`
                }
            }

            const peer = participants.current.get(peerUserUrn)
            if (peer) {
                if (peer.isGuest) {
                    return `${peer.displayName} (guest)`
                }
                return peer.displayName ?? peer.username
            }

            const peer2 = roomStateRef.current.peerUserProfileCache[peerUserUrn]
            if (peer2) {
                if (peer2.isGuest) {
                    return `${peer2.displayName} (guest)`
                }
                return peer2.username
            }

            return null
        },
        [isEnabled, userUrn, roomStateRef]
    )

    const setOnFileDownloadCompleteHandler = (handler: (fileUuid: string, filename: string) => void) => {
        onFileDownloadComplete.current = handler
    }

    const setOnNewMessageHandler = (handler: (chatmsg: ChatMessage) => void) => {
        onNewMessage.current = handler
    }

    return {
        syncCalled,
        isLoading,

        roomUrn,
        userUrn,

        messageHistory,

        messagePendingLookup,
        messagePendingLookupRef,

        fileUploadProgressLookup,
        fileDownloadProgressLookup,

        participants,
        fileUploadCancelTokenLookup,
        stats,

        loadMessages,
        sendText,
        sendFile,
        deleteMessages,
        cancelFileUpload,
        cancelFileDownload,
        triggerDownloadFile,
        resolveMessageUsername,

        setOnFileDownloadCompleteHandler,
        setOnNewMessageHandler,
    }
}

/**
 * This function appends to messageHistory *in place*.
 * Be sure to send in a copy of the messageHistory array if you are appending a React state variable.
 * @returns
 */
const mergeIntoMessageHistory = (messageHistory: ChatMessage[], chatmsg: ChatMessage): ChatMessage[] => {
    let i = messageHistory.length
    for (let m of messageHistory) {
        // console.log(`merge: chatmsg.timestamp=${chatmsg.timestamp} m.timestamp=${m.timestamp}`)
        if (chatmsg.id === m.id) {
            continue
        }
        if (chatmsg.timestamp > m.timestamp) {
            // console.log(`merge: splicing at chatmsg.body="${chatmsg.body}" i=${i}`)
            messageHistory.splice(i, 0, chatmsg)
            // console.log(`merge: after splice: messageHistory=%O`, messageHistory)
            return messageHistory
        }
        --i
    }

    messageHistory.splice(0, 0, chatmsg)
    return messageHistory
}

export default useCentralizedChatManager
